import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle2, ArrowRight, CalendarCheck, Cpu, BarChart3, BellRing, Sparkles, LayoutDashboard, BookOpen, X, Award, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { subscribeTeachers, preloadImage, TeacherBookSlider } from './introTeachers';
import { TeacherItem, MainBannerModel, MainVideoSettingModel, COLLECTIONS } from '../../../models';
import { db } from '../../../firebase';
import { collection, onSnapshot, query, orderBy, doc } from 'firebase/firestore';

interface IntroHomeProps {
    onStart: () => void;
    onShowInquiry?: () => void;
}

const DEFAULT_BANNERS: MainBannerModel[] = [
    {
        id: 'default-1',
        title: '신뢰를 더해가는 성장의 공식',
        titleHighlight: '정성우학원이 만들어갑니다.',
        subtitle: '정성우학원은 학생의 주도적 학습 성장을 돕고, 학부모님께는 실시간 학습 이행 현황 및 신뢰를 전달합니다.',
        badgeText: '개인별 맞춤 학습 관리',
        imageUrl: '/main-bg.jpg',
        buttonText: '상담신청',
        buttonLink: 'inquiry',
        order: 1,
        isActive: true,
        overlayOpacity: 35,
        createdAt: 0
    },
    {
        id: 'default-2',
        title: '초·중·고 수학 / 과학 / 국어 / 영어',
        titleHighlight: '과목별 1:1 밀착 학습 관리',
        subtitle: '학생 개개인의 성향과 학업 수준을 정밀 분석하여 최적의 오답 관리와 클리닉 시스템을 제공합니다.',
        badgeText: '명품 강사진 & 맞춤 클리닉',
        imageUrl: '/system_oneonone.jpg',
        buttonText: '상담신청',
        buttonLink: 'inquiry',
        order: 2,
        isActive: true,
        overlayOpacity: 35,
        createdAt: 0
    },
    {
        id: 'default-3',
        title: '학생의 꿈을 현실로 만드는',
        titleHighlight: '정성우학원 최상의 교육 환경',
        subtitle: '철저한 수능/내신 대비와 꼼꼼한 피드백으로 놀라운 성적 향상 신화를 만들어갑니다.',
        badgeText: '수원 영통 대표 학원',
        imageUrl: '/academy_entrance.png',
        buttonText: '상담신청',
        buttonLink: 'inquiry',
        order: 3,
        isActive: true,
        overlayOpacity: 35,
        createdAt: 0
    }
];

export const getYoutubeEmbedUrl = (url?: string): string | null => {
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    const regExp = /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/;
    const match = trimmed.match(regExp);
    if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=0&rel=0`;
    }
    if (/^[\w-]{11}$/.test(trimmed)) {
        return `https://www.youtube.com/embed/${trimmed}?autoplay=0&rel=0`;
    }
    return null;
};

const getInitialMainVideo = (): { data: MainVideoSettingModel; isLoaded: boolean } => {
    try {
        const cached = localStorage.getItem('jsw_main_video_cache');
        if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && typeof parsed.youtubeUrl === 'string') {
                return { data: parsed, isLoaded: true };
            }
        }
    } catch (e) {
        // ignore
    }
    return {
        data: {
            youtubeUrl: '',
            title: '정성우학원 강사 소개 영상',
            isActive: true
        },
        isLoaded: false
    };
};

const initialVideoState = getInitialMainVideo();

const IntroHome: React.FC<IntroHomeProps> = ({ onStart, onShowInquiry }) => {
    const [teachers, setTeachers] = useState<TeacherItem[]>([]);
    const [selectedTeacher, setSelectedTeacher] = useState<TeacherItem | null>(null);
    const [banners, setBanners] = useState<MainBannerModel[]>(DEFAULT_BANNERS);
    const [mainVideo, setMainVideo] = useState<MainVideoSettingModel>(initialVideoState.data);
    const [isVideoLoaded, setIsVideoLoaded] = useState(initialVideoState.isLoaded);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const touchStartX = useRef<number | null>(null);
    const touchEndX = useRef<number | null>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (touchStartX.current !== null && touchEndX.current !== null) {
            const diff = touchStartX.current - touchEndX.current;
            if (diff > 40) {
                handleNextSlide();
            } else if (diff < -40) {
                handlePrevSlide();
            }
        }
        touchStartX.current = null;
        touchEndX.current = null;
    };

    // Subscribe to Main Video Settings
    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'settings', 'mainVideo'), (snap) => {
            if (snap.exists()) {
                const data = snap.data() as MainVideoSettingModel;
                setMainVideo(data);
                setIsVideoLoaded(true);
                try {
                    localStorage.setItem('jsw_main_video_cache', JSON.stringify(data));
                } catch (e) {}
            } else {
                const fallbackData: MainVideoSettingModel = {
                    youtubeUrl: 'https://www.youtube.com/watch?v=1TAevjH74QM',
                    title: '정성우학원 강사 소개 영상',
                    isActive: true
                };
                setMainVideo(fallbackData);
                setIsVideoLoaded(true);
            }
        }, (err) => {
            console.warn('Error fetching main video settings:', err);
            setIsVideoLoaded(true);
        });
        return () => unsub();
    }, []);

    // Subscribe to Main Banners
    useEffect(() => {
        const q = query(collection(db, COLLECTIONS.MAIN_BANNERS), orderBy('order', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: MainBannerModel[] = [];
            snapshot.forEach((docSnap) => {
                const bData = { id: docSnap.id, ...docSnap.data() } as MainBannerModel;
                if (bData.isActive) {
                    list.push(bData);
                }
            });

            if (list.length > 0) {
                setBanners(list);
                list.forEach(b => {
                    if (b.imageUrl) preloadImage(b.imageUrl);
                    if (b.mobileImageUrl) preloadImage(b.mobileImageUrl);
                });
            } else {
                setBanners(DEFAULT_BANNERS);
            }
        }, (err) => {
            console.warn('Firestore banner fetch error, fallback to defaults:', err);
            setBanners(DEFAULT_BANNERS);
        });

        return () => unsubscribe();
    }, []);

    // Auto Slide Timer (3 Seconds)
    useEffect(() => {
        if (isHovered || banners.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentSlideIndex((prev) => (prev + 1) % banners.length);
        }, 3000);

        return () => clearInterval(interval);
    }, [banners, isHovered]);

    const currentBanner = banners[currentSlideIndex] || banners[0] || DEFAULT_BANNERS[0];

    const handlePrevSlide = () => {
        setCurrentSlideIndex((prev) => (prev - 1 + banners.length) % banners.length);
    };

    const handleNextSlide = () => {
        setCurrentSlideIndex((prev) => (prev + 1) % banners.length);
    };

    useEffect(() => {
        const unsubscribe = subscribeTeachers((list) => {
            setTeachers(list);
        });
        return () => unsubscribe();
    }, []);


    useEffect(() => {
        const container = containerRef.current;
        if (!container || teachers.length === 0) return;

        let animationFrameId: number;
        let isDown = false;
        let startX: number;
        let scrollLeft: number;

        // Slow auto-scroll speed (pixels per frame)
        const scrollSpeed = 0.5;

        const step = () => {
            if (!isDown && container) {
                container.scrollLeft += scrollSpeed;
                // Loop back seamlessly when scrolled half the total width of repeated track
                if (container.scrollLeft >= container.scrollWidth / 2) {
                    container.scrollLeft = 0;
                }
            }
            animationFrameId = requestAnimationFrame(step);
        };

        animationFrameId = requestAnimationFrame(step);

        // Desktop mouse drag to scroll handlers
        const handleMouseDown = (e: MouseEvent) => {
            isDown = true;
            container.style.cursor = 'grabbing';
            startX = e.pageX - container.offsetLeft;
            scrollLeft = container.scrollLeft;
        };

        const handleMouseLeave = () => {
            isDown = false;
            container.style.cursor = 'grab';
        };

        const handleMouseUp = () => {
            isDown = false;
            container.style.cursor = 'grab';
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - container.offsetLeft;
            const walk = (x - startX) * 2;
            container.scrollLeft = scrollLeft - walk;
        };

        // Pause auto-scroll on manual touch events
        const handleTouchStart = () => {
            isDown = true;
        };

        const handleTouchEnd = () => {
            isDown = false;
        };

        container.addEventListener('mousedown', handleMouseDown);
        container.addEventListener('mouseleave', handleMouseLeave);
        container.addEventListener('mouseup', handleMouseUp);
        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchend', handleTouchEnd, { passive: true });

        // Default cursor style
        container.style.cursor = 'grab';

        return () => {
            cancelAnimationFrame(animationFrameId);
            container.removeEventListener('mousedown', handleMouseDown);
            container.removeEventListener('mouseleave', handleMouseLeave);
            container.removeEventListener('mouseup', handleMouseUp);
            container.removeEventListener('mousemove', handleMouseMove);
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, [teachers]);

    return (
        <div className="animate-in fade-in duration-1000">
            {/* Custom Marquee Styles for hiding scrollbars globally */}
            <style dangerouslySetInnerHTML={{__html: `
                .scrollbar-none::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-none {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}} />

            {/* YouTube Video Section (Top Full Width Widescreen) */}
            {isVideoLoaded && mainVideo.isActive && getYoutubeEmbedUrl(mainVideo.youtubeUrl) && (
                <section className="w-full bg-[#080D1E] border-b border-indigo-950/30 relative overflow-hidden">
                    <div className="w-full max-w-[1920px] mx-auto aspect-video relative bg-slate-950 overflow-hidden shadow-2xl">
                        <iframe
                            key={getYoutubeEmbedUrl(mainVideo.youtubeUrl)!}
                            src={getYoutubeEmbedUrl(mainVideo.youtubeUrl)!}
                            title={mainVideo.title || "정성우학원 강사 소개 영상"}
                            className="w-full h-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        />
                    </div>
                </section>
            )}

            {/* Hero Main Banner Slider Section (Strict 16:9 Aspect Ratio) */}
            <section 
                className="relative w-full bg-[#250b53] flex items-center justify-center overflow-hidden select-none group"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Banner Container: Strict 16:9 Widescreen Ratio (aspect-video) across ALL devices */}
                <div className="relative w-full max-w-[1920px] mx-auto aspect-video flex items-center justify-center bg-[#250b53] overflow-hidden">
                    {/* Background Banner Slides with smooth cross-fade */}
                    {banners.map((b, idx) => (
                        <div
                            key={b.id}
                            onClick={() => {
                                if (!b.buttonLink || b.buttonLink === 'inquiry') {
                                    if (onShowInquiry) onShowInquiry();
                                    else onStart();
                                } else if (b.buttonLink.startsWith('http')) {
                                    window.open(b.buttonLink, '_blank');
                                } else {
                                    window.location.href = b.buttonLink;
                                }
                            }}
                            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out cursor-pointer flex items-center justify-center ${
                                idx === currentSlideIndex ? 'opacity-100 scale-100 z-10' : 'opacity-0 pointer-events-none scale-105 transition-transform duration-1000'
                            }`}
                        >
                            <picture className="w-full h-full block">
                                {b.mobileImageUrl && (
                                    <source media="(max-width: 640px)" srcSet={b.mobileImageUrl} />
                                )}
                                <img
                                    src={b.imageUrl}
                                    alt={b.title}
                                    loading={idx === 0 ? 'eager' : 'lazy'}
                                    decoding="async"
                                    {...(idx === 0 ? { fetchPriority: 'high' } : {})}
                                    className="w-full h-full object-cover object-center"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = '/main-bg.jpg';
                                    }}
                                />
                            </picture>
                            {b.overlayOpacity && b.overlayOpacity > 0 ? (
                                <div
                                    className="absolute inset-0 bg-slate-950/20 pointer-events-none"
                                    style={{ opacity: b.overlayOpacity / 100 }}
                                />
                            ) : null}
                        </div>
                    ))}

                    {/* Left & Right Control Arrows */}
                    {banners.length > 1 && (
                        <>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrevSlide();
                                }}
                                className="absolute left-1.5 sm:left-4 md:left-6 top-1/2 -translate-y-1/2 z-30 w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-slate-900/60 hover:bg-indigo-600/90 text-white/80 hover:text-white backdrop-blur-md border border-white/10 flex items-center justify-center transition-all opacity-70 hover:opacity-100 hover:scale-110 active:scale-95 shadow-lg cursor-pointer"
                                aria-label="Previous Slide"
                            >
                                <ChevronLeft size={18} className="sm:w-6 sm:h-6" />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNextSlide();
                                }}
                                className="absolute right-1.5 sm:right-4 md:right-6 top-1/2 -translate-y-1/2 z-30 w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-slate-900/60 hover:bg-indigo-600/90 text-white/80 hover:text-white backdrop-blur-md border border-white/10 flex items-center justify-center transition-all opacity-70 hover:opacity-100 hover:scale-110 active:scale-95 shadow-lg cursor-pointer"
                                aria-label="Next Slide"
                            >
                                <ChevronRight size={18} className="sm:w-6 sm:h-6" />
                            </button>
                        </>
                    )}
                </div>
            </section>

            {/* Teacher Marquee Slider Section */}
            {teachers.length > 0 && (
                <section className="w-full bg-[#080D1E] py-8 border-y border-indigo-950/20 overflow-hidden relative">
                    {/* Shadow indicators for horizontal sliding */}
                    <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#0B132B] to-transparent z-10 pointer-events-none"></div>
                    <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#0B132B] to-transparent z-10 pointer-events-none"></div>

                    <div className="max-w-5xl mx-auto px-4 md:px-8 mb-4 text-center sm:text-left">
                        <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Our Instructors</span>
                        <h3 className="text-sm sm:text-base font-black text-slate-300 mt-1">정성우학원을 이끄는 명품 강사진을 소개합니다</h3>
                    </div>

                    {/* Interactive drag-to-scroll track */}
                    <div 
                        ref={containerRef}
                        className="w-full overflow-x-auto scrollbar-none flex gap-5 px-4 py-2 select-none"
                    >
                        {(() => {
                            // Repeat the list 4 times to ensure seamless infinite looping scrollWidth
                            const repeatedList = [...teachers, ...teachers, ...teachers, ...teachers];
                            return repeatedList.map((teacher, index) => (
                                <div 
                                    key={`teacher-${teacher.id}-${index}`}
                                    draggable="false"
                                    onClick={() => setSelectedTeacher(teacher)}
                                    className="flex flex-col bg-[#111A36]/45 backdrop-blur-md border border-indigo-950/50 rounded-[2rem] p-4 w-72 h-[31.5rem] shrink-0 shadow-lg hover:border-indigo-500/25 transition-all duration-300 group cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    <div className="w-full h-76 rounded-[1.5rem] overflow-hidden shrink-0 bg-slate-900 border border-indigo-500/15 shadow-inner pointer-events-none relative">
                                        {teacher.hideMainPhoto ? (
                                            <div className="w-full h-full bg-gradient-to-br from-indigo-950/90 via-slate-900 to-indigo-900/60 flex flex-col items-center justify-center p-6 text-center select-none">
                                                <div className="w-20 h-20 rounded-full bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-3 shadow-inner">
                                                    <User size={36} />
                                                </div>
                                                <span className="text-xs font-black text-indigo-300 tracking-wider">정성우학원</span>
                                                <span className="text-[11px] font-bold text-slate-400 mt-1">{teacher.name} 강사</span>
                                            </div>
                                        ) : (
                                            <img src={teacher.imageUrl} alt={teacher.name} draggable="false" loading="lazy" decoding="async" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 pointer-events-none" />
                                        )}
                                        {teacher.youtubeUrl && (
                                            <div className="absolute top-3 left-3 bg-red-600/90 text-white rounded-lg px-2 py-1 flex items-center gap-1.5 shadow-md z-30">
                                                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                                                    <path d="M23.498 6.163c-.272-1.022-1.074-1.826-2.099-2.099C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.399.564C1.577 4.337.775 5.141.502 6.163 0 8.07 0 12 0 12s0 3.93.502 5.837c.272 1.022 1.074 1.826 2.099 2.099C4.456 20.5 12 20.5 12 20.5s7.544 0 9.399-.564c1.025-.273 1.827-1.077 2.099-2.099C24 15.93 24 12 24 12s0-3.93-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                </svg>
                                                <span className="text-[10px] font-black tracking-wider uppercase">OT</span>
                                            </div>
                                        )}
                                        {/* Representative Textbook Cover overlapping in the bottom-right corner */}
                                        {teacher.booksList && teacher.booksList.length > 0 && teacher.booksList[0].imageUrl && (
                                            <div className="absolute bottom-3 right-3 w-16 aspect-[3/4] rounded-lg overflow-hidden border-2 border-indigo-500/20 shadow-xl shadow-black/60 transform rotate-6 group-hover:rotate-0 group-hover:scale-105 transition-all duration-300 z-20 bg-slate-950">
                                                <img
                                                    src={teacher.booksList[0].imageUrl}
                                                    alt={teacher.booksList[0].name}
                                                    draggable="false"
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-left mt-4 flex-1 flex flex-col justify-between pointer-events-none">
                                        <div className="space-y-2 pointer-events-none">
                                            <div className="flex items-center justify-between pointer-events-none">
                                                <span className="text-base font-black text-slate-100">{teacher.name} 선생님</span>
                                                <span className="px-2.5 py-0.5 rounded bg-indigo-500/10 text-indigo-300 font-black text-[10px] uppercase tracking-wider shrink-0">{teacher.subject || '전과목'}</span>
                                            </div>
                                            <p className="text-slate-350 font-medium text-xs leading-relaxed line-clamp-2">{teacher.intro || '정성우학원 대표 강사'}</p>
                                        </div>
                                        <div className="border-t border-indigo-950/30 pt-3 mt-3 pointer-events-none">
                                            <p className="text-slate-500 font-medium text-[11px] truncate leading-none">{teacher.career.split('\n')[0] || ''}</p>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>
                </section>
            )}

            {/* Content Section (constrained width) */}
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-20 space-y-16 md:space-y-24">
                {/* Learning Management Pillars Section */}
                <div className="space-y-12 md:space-y-16">
                    <div className="space-y-6 md:space-y-8 flex flex-col items-center w-full">
                        <div className="text-center space-y-4">
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight break-keep">
                                학습 성장을 위한 <span className="text-indigo-400">3가지 핵심 데이터</span> 관리 시스템
                            </h2>
                            <p className="text-slate-450 font-medium text-xs sm:text-sm max-w-xl mx-auto break-keep">
                                학습 일정부터 성적 추이, 질문답변 기록까지 모든 학습 데이터를 체계적으로 수집하고 정밀하게 분석하여, 학생들의 개인별 취약점을 정확하게 파악하고 빈틈없는 맞춤형 성장을 이끌어냅니다.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {[
                            {
                                icon: CalendarCheck,
                                title: "정밀 스케줄링",
                                desc: "개인별 학습 속도와 목표에 맞춘 최적의 계획 수립 및 실시간 이행률 모니터링",
                                color: "bg-blue-500/10 text-blue-400",
                                accent: "border-blue-900/30"
                            },
                            {
                                icon: BarChart3,
                                title: "심층 성적 분석",
                                desc: "정밀 채점 및 분석으로 취약 과목 및 단원 완벽 파악",
                                color: "bg-amber-500/10 text-amber-400",
                                accent: "border-amber-900/30"
                            },
                            {
                                icon: Cpu,
                                title: "실시간 질문답변",
                                desc: "질문답변 시스템을 통해 모르는 문제는 막힘없이 즉각적인 개별 피드백 지원",
                                color: "bg-purple-500/10 text-purple-400",
                                accent: "border-purple-900/30"
                            }
                        ].map((pillar, idx) => (
                            <div
                                key={idx}
                                className={`relative p-6 sm:p-8 rounded-[2.5rem] bg-[#111A36]/30 border border-indigo-950/40 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group overflow-hidden flex flex-col justify-between`}
                            >
                                <div>
                                    <div className={`w-12 h-12 ${pillar.color} rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 group-hover:rotate-3`}>
                                        <pillar.icon size={24} />
                                    </div>
                                    <h3 className="text-base sm:text-lg font-black text-slate-200 mb-3">{pillar.title}</h3>
                                    <p className="text-slate-450 font-medium text-xs sm:text-sm leading-relaxed">{pillar.desc}</p>
                                </div>

                                <div className="absolute -right-4 -bottom-4 opacity-[0.03] text-slate-100 transform rotate-12 group-hover:scale-125 transition-transform duration-700 pointer-events-none">
                                    <pillar.icon size={100} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Benefit Split Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                    <div className="bg-gradient-to-br from-indigo-950/60 to-indigo-900/40 border border-indigo-500/15 p-6 sm:p-8 rounded-[2.5rem] text-white space-y-6 shadow-2xl shadow-indigo-950/40 relative overflow-hidden group">
                        <div className="relative z-10">
                            <span className="bg-white/10 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border border-white/5">For Students</span>
                            <h3 className="text-lg sm:text-xl font-black mt-4 mb-4 break-keep">공부가 쉬워지는 <br />나만의 학습 도우미</h3>
                            <ul className="space-y-3 opacity-90 font-medium text-xs sm:text-sm">
                                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-400 shrink-0" /> 매일 무엇을 공부할지 고민할 필요가 없어요</li>
                                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-400 shrink-0" /> 모르는 문제는 선생님이 1:1로 즉시 알려줘요</li>
                                <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-indigo-400 shrink-0" /> 나의 실력이 쌓이는 걸 그래프로 확인하세요</li>
                            </ul>
                        </div>
                        <Sparkles className="absolute -right-8 -bottom-8 w-40 h-40 opacity-10 rotate-12 group-hover:scale-110 transition-transform duration-700" />
                    </div>

                    <div className="bg-[#111A36]/30 border border-indigo-950/40 p-6 sm:p-8 rounded-[2.5rem] shadow-xl space-y-6 relative overflow-hidden group">
                        <div className="relative z-10">
                            <span className="bg-slate-800/50 text-slate-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border border-slate-700/10">For Parents</span>
                            <h3 className="text-lg sm:text-xl font-black mt-4 mb-4 text-white break-keep">학원 생활이 <br />한눈에 보이는 안심 관리</h3>
                            <ul className="space-y-3 text-slate-400 font-medium text-xs sm:text-sm">
                                <li className="flex items-center gap-2 text-slate-350"><CheckCircle2 size={14} className="text-indigo-400 shrink-0" /> 학원의 모든 소식을 실시간 알림으로 받아요</li>
                                <li className="flex items-center gap-2 text-slate-350"><CheckCircle2 size={14} className="text-indigo-400 shrink-0" /> 자녀의 학습 이행 현황을 모바일로 확인하세요</li>
                                <li className="flex items-center gap-2 text-slate-350"><CheckCircle2 size={14} className="text-indigo-400 shrink-0" /> 체계적인 분석 리포트로 믿고 맡길 수 있어요</li>
                            </ul>
                        </div>
                        <LayoutDashboard className="absolute -right-8 -bottom-8 w-40 h-40 opacity-[0.02] rotate-12 group-hover:scale-110 transition-transform duration-700" />
                    </div>
                </div>
            </div>

            {/* Teacher Detail Modal */}
            {selectedTeacher && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300 cursor-pointer"
                        onClick={() => setSelectedTeacher(null)}
                    ></div>
                    <div className="relative w-full max-w-6xl bg-[#0F162A]/95 border border-indigo-950/50 rounded-[2.5rem] shadow-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300 text-left flex flex-col md:flex-row gap-8 items-start select-text">
                        {/* Close button */}
                        <button 
                            onClick={() => setSelectedTeacher(null)} 
                            className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors z-10 w-8 h-8 rounded-full bg-slate-900/50 flex items-center justify-center border border-indigo-950/40 cursor-pointer"
                        >
                            <X size={18} />
                        </button>

                        {/* Left Side: Photo */}
                        <div className="w-full md:w-64 lg:w-72 shrink-0 flex flex-col items-center justify-center md:sticky md:top-0">
                            <div className="w-full max-w-[280px] md:max-w-none aspect-[3/4] rounded-[2rem] overflow-hidden bg-slate-950 border border-indigo-950/40 shadow-lg">
                                {selectedTeacher.hideMainPhoto ? (
                                    <div className="w-full h-full bg-gradient-to-br from-indigo-950/90 via-slate-900 to-indigo-900/60 flex flex-col items-center justify-center p-6 text-center select-none min-h-[280px]">
                                        <div className="w-20 h-20 rounded-full bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-3 shadow-inner">
                                            <User size={40} />
                                        </div>
                                        <span className="text-sm font-black text-indigo-300 tracking-wider">정성우학원</span>
                                        <span className="text-xs font-bold text-slate-400 mt-1">{selectedTeacher.name} 선생님</span>
                                    </div>
                                ) : (
                                    <img
                                        src={selectedTeacher.imageUrl}
                                        alt={selectedTeacher.name}
                                        className="w-full h-full object-cover object-top"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Right Side: Details */}
                        <div className="flex-1 space-y-6 w-full flex flex-col justify-between">
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 flex-wrap">
                                    {selectedTeacher.subject && (
                                        <span className="px-2.5 py-1 rounded-lg bg-indigo-500/10 text-indigo-400 text-xs font-black border border-indigo-500/20">
                                            {selectedTeacher.subject}
                                        </span>
                                    )}
                                    <h4 className="text-xl sm:text-2xl font-black text-slate-100">{selectedTeacher.name} 선생님</h4>
                                </div>
                                {selectedTeacher.intro && (
                                    <p className="text-sm sm:text-base text-indigo-300 font-bold mt-2.5 leading-relaxed">
                                        "{selectedTeacher.intro}"
                                    </p>
                                )}
                                {(selectedTeacher.career || getYoutubeEmbedUrl(selectedTeacher.youtubeUrl)) && (
                                    <div className={`grid grid-cols-1 ${getYoutubeEmbedUrl(selectedTeacher.youtubeUrl) ? 'lg:grid-cols-12' : ''} gap-6 border-t border-indigo-950/30 pt-4`}>
                                        {selectedTeacher.career && (
                                            <div className={getYoutubeEmbedUrl(selectedTeacher.youtubeUrl) ? 'lg:col-span-7 space-y-2' : 'space-y-2'}>
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5 flex items-center gap-1.5">
                                                    <Award size={12} className="text-indigo-400" />
                                                    약력 및 경력
                                                </p>
                                                <p className="text-xs sm:text-sm text-slate-350 font-medium leading-relaxed whitespace-pre-line">
                                                    {selectedTeacher.career}
                                                </p>
                                            </div>
                                        )}
                                        {getYoutubeEmbedUrl(selectedTeacher.youtubeUrl) && (
                                            <div className="lg:col-span-5 space-y-2">
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-0.5 flex items-center gap-1.5">
                                                    <svg className="w-3.5 h-3.5 fill-indigo-400" viewBox="0 0 24 24">
                                                        <path d="M23.498 6.163c-.272-1.022-1.074-1.826-2.099-2.099C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.399.564C1.577 4.337.775 5.141.502 6.163 0 8.07 0 12 0 12s0 3.93.502 5.837c.272 1.022 1.074 1.826 2.099 2.099C4.456 20.5 12 20.5 12 20.5s7.544 0 9.399-.564c1.025-.273 1.827-1.077 2.099-2.099C24 15.93 24 12 24 12s0-3.93-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                    </svg>
                                                    소개 및 OT 영상
                                                </p>
                                                <div className="w-full aspect-video rounded-2xl overflow-hidden bg-slate-950 border border-indigo-950/40 shadow-md">
                                                    <iframe
                                                        src={getYoutubeEmbedUrl(selectedTeacher.youtubeUrl)!}
                                                        title={`${selectedTeacher.name} 선생님 OT 영상`}
                                                        className="w-full h-full border-0"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Textbooks showcase - Compact Auto/Manual Carousel Slider */}
                            {((selectedTeacher.booksList && selectedTeacher.booksList.length > 0) || selectedTeacher.bookName) && (
                                <TeacherBookSlider 
                                    books={selectedTeacher.booksList}
                                    singleBookName={selectedTeacher.bookName}
                                    singleBookDesc={selectedTeacher.bookDesc}
                                    singleBookImageUrl={selectedTeacher.bookImageUrl}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntroHome;
